import { useMemo } from 'react';
import { IRccap } from '@nkr/models/index';
import RankRenderer from '@nkr/views/admin/center/list/common/rank-renderer';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ColDef, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'react-bootstrap';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';

interface IEditRendererParams extends ICellRendererParams {
    onEdit: (id: number) => void
}

interface IProps {
    data: IRccap[],
    onEdit: (id: number) => void,
    onGridReady: (e: GridReadyEvent) => void
}

const Grid = (props: IProps) => {
    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const columns: ColDef[] = [
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Rank</SortableHeaderComponent>,
            headerName: 'Rank',
            field: 'id',
            width: 60,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            cellRendererFramework: RankRenderer
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Responsible<br />Center</SortableHeaderComponent>,
            headerName: 'Responsible Center',
            field: 'responsibleCenterAlias',
            width: 150,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Center</SortableHeaderComponent>,
            headerName: 'Donor Center',
            field: 'donorCenterAlias',
            width: 150,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Center</SortableHeaderComponent>,
            headerName: 'Recipient Center',
            field: 'recipientCenterAlias',
            width: 150,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap #</SortableHeaderComponent>,
            headerName: 'Swap #',
            field: 'swapChainNumber',
            width: 100,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.data.swapChainNumber}/{params.data.swapClusterNumber}/{params.data.swapPositionNumber}</>
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Phase</SortableHeaderComponent>,
            headerName: 'Phase',
            field: 'phase',
            width: 70,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>XPs<br />Affected</SortableHeaderComponent>,
            headerName: 'XPs Affected',
            field: 'transplantsImpacted',
            width: 85,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Primary Cause</SortableHeaderComponent>,
            headerName: 'Primary Cause',
            field: 'rootCausePrimary',
            width: 300,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Secondary Cause</SortableHeaderComponent>,
            headerName: 'Secondary Cause',
            field: 'rootCauseSecondary',
            width: 300,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Response<br />Deadline</SortableHeaderComponent>,
            headerName: 'Response Deadline',
            field: 'dueDate',
            width: 120,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Days Late</SortableHeaderComponent>,
            headerName: 'Days Late',
            field: 'daysLate',
            width: 100,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Manager</SortableHeaderComponent>,
            headerName: 'Manager',
            field: 'rccapManager',
            width: 150,
            cellClass: 'rccap-list-cell',
            headerClass: 'rccap-list-header',
            sortable: true
        },
        {
            headerName: '',
            field: 'id',
            width: 100,
            cellClass: 'action-menu',
            cellRendererParams: { onEdit: props.onEdit },
            cellRendererFramework: (params: IEditRendererParams) => <><Button bsSize="xs" bsStyle="primary" className="btn-block" onClick={() => params.onEdit(params.value)}>Edit</Button></>
        }
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className="container-fluid ag-theme-bootstrap">
            <AgGridReact
                columnDefs={columnDefs}
                onGridReady={props.onGridReady}
                rowData={rowData}
                headerHeight={45}
                rowHeight={35}
                noRowsOverlayComponentFramework={() => <div>No RCCAPs found...</div>}
                suppressCellSelection={true}
                onRowDataChanged={onRowDataChanged}
            >
            </AgGridReact>
        </GridWrapper>
    );
}

export default Grid;